import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, EchartsWithTheme, Empty, Modal, Select, Tabs, useTheme } from '@maxtropy/components';
import styles from './index.module.scss';
import PowerIcon from '../../imgs/power.png';
import { getChartOption, getPowerChartOption } from './chart';
import { isNil } from 'lodash-es';
import {
  apiV2PowerRoomConfigCircuitListWithRoomIdPost,
  apiV2PowerRoomConfigElectricityConsumptionSituationPost,
  apiV2DistributionCabinetRoomMonitorListPost,
  V2DistributionCabinetRoomMonitorListPostResponse,
  // /api/v2/distribution-cabinet/monitor/code2Url
  apiV2DistributionCabinetMonitorCode2UrlPost,
} from '@maxtropy/device-customer-apis-v2';
import dayjs, { Dayjs } from 'dayjs';
import { TimeDataItem } from '../../utils';
import { keepTwoNull } from '@/utils/utils';
import { FullscreenOutlined } from '@ant-design/icons';
import { HlsVideo } from '@/components/RecorderPreview';
import { Spin } from 'antd';

interface RightContentProps {
  roomId?: number;
  isConfigurationMonitoring?: boolean;
}

interface MonitorOption {
  label: string;
  value: string;
  url?: string;
}

export type MonitorItem = Exclude<V2DistributionCabinetRoomMonitorListPostResponse['list'], undefined>[number];

const RightContent: React.FC<RightContentProps> = ({ roomId, isConfigurationMonitoring }) => {
  const theme = useTheme();
  const [tabItems, setTabItems] = useState<any[]>([]);
  const [activeKey, setActiveKey] = useState<string>();
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [chartData, setChartData] = useState<TimeDataItem[]>([]);
  const [paramList, setParamList] = useState<any>();
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [monitorList, setMonitorList] = useState<MonitorItem[]>();
  const [selectedMonitor, setSelectedMonitor] = useState<MonitorOption>();
  const [monitorUrl, setMonitorUrl] = useState<string>();
  const [videoLoading, setVideoLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isNil(roomId)) return;
    setTabItems([]);
    apiV2PowerRoomConfigCircuitListWithRoomIdPost({ id: roomId }).then(res => {
      const list = (res.list ?? []).map(m => ({ key: String(m.id), label: m.name! }));
      setTabItems(list);

      setActiveKey(list?.[0]?.key);
    });
  }, [roomId]);

  useEffect(() => {
    setMonitorList([]);
    setSelectedMonitor(undefined);
    if (isNil(roomId) || !isConfigurationMonitoring) {
      return;
    }
    setVideoLoading(true);
    apiV2DistributionCabinetRoomMonitorListPost({
      id: roomId,
    })
      .then(res => {
        setMonitorList(res.list ?? []);
      })
      .finally(() => setVideoLoading(false));
  }, [roomId, isConfigurationMonitoring]);

  useEffect(() => {
    setParamList(undefined);
    setChartData([]);
    if (isNil(activeKey) || isNil(date)) return;
    apiV2PowerRoomConfigElectricityConsumptionSituationPost({
      id: Number(activeKey),
      time: dayjs(date).valueOf(),
    }).then(res => {
      setParamList({
        avgPowerFactor: res?.avgPowerFactor,
        realTimePowerFactor: res?.realTimePowerFactor,
        isRealTimePowerFactor: res?.isRealTimePowerFactor,
        avgFrequency: res?.avgFrequency,
        realTimeFrequency: res?.realTimeFrequency,
        isRealTimeFrequency: res?.isRealTimeFrequency,
      });
      setChartData(res.circuitElectricityConsumptionSituationInfoList ?? []);
    });
  }, [activeKey, date]);

  useEffect(() => {
    if (selectedMonitor) {
      apiV2DistributionCabinetMonitorCode2UrlPost({ code: selectedMonitor?.value }).then(res => {
        setMonitorUrl(res.url);
      });
    } else {
      setMonitorUrl(undefined);
    }
  }, [selectedMonitor]);

  const chartOption = useMemo(() => getChartOption(chartData), [chartData]);

  const powerChartOption = useMemo(() => getPowerChartOption(chartData), [chartData]);

  const monitorListOption = useMemo(() => {
    const data = (monitorList ?? []).map(i => ({ label: i.name!, value: i.code!, url: i.url }));
    setSelectedMonitor(data?.[0] ?? undefined);
    return data;
  }, [monitorList]);

  const disabledDate = (current: Dayjs) => {
    return current && current >= dayjs().endOf('day');
  };

  return (
    <div className={styles.right}>
      <div className={styles.titleRow}>
        <img src={PowerIcon} alt=""></img>
        <div className={styles.title}>用电情况</div>
        <DatePicker value={date} style={{ width: 160 }} disabledDate={disabledDate} onChange={setDate} />
      </div>
      {tabItems.length ? (
        <>
          <Tabs activeKey={activeKey} items={tabItems} onChange={setActiveKey} />
          <div className={styles.row}>
            <div style={{ width: '49%' }}>
              <span className={styles.smallTitle}>功率因数</span>
              <span className={styles.label}>
                实时：<span className={styles.realTime}>{keepTwoNull(paramList?.realTimePowerFactor)}</span>
              </span>
            </div>
            <div className={styles.splitLine}></div>
            <div>
              <span className={styles.smallTitle}>频率</span>
              <span className={styles.label}>
                实时：<span className={styles.realTime}>{keepTwoNull(paramList?.realTimeFrequency)}</span>
              </span>
            </div>
          </div>
          <div className={styles.echartsCon}>
            <div className={styles.echartsItem}>
              <EchartsWithTheme
                empty={{
                  isShowEmpty: false,
                  emptyNode: <Empty style={{ marginTop: 80 }} />,
                }}
                option={powerChartOption}
                style={{ height: '100%' }}
                theme={theme}
              />
            </div>
            <div className={styles.echartsItem}>
              <EchartsWithTheme
                empty={{
                  isShowEmpty: false,
                  emptyNode: <Empty style={{ marginTop: 80 }} />,
                }}
                option={chartOption}
                style={{ height: '100%' }}
                theme={theme}
              />
            </div>
          </div>
        </>
      ) : (
        <Empty description="暂无数据" style={{ paddingTop: 120, height: 472, flex: 1 }} />
      )}
      {isConfigurationMonitoring && (
        <div>
          <Spin spinning={videoLoading}>
            <div className={styles.titleRow}>
              <img src={PowerIcon} alt=""></img>
              <div className={styles.title}>
                视频监控
                <Select
                  options={monitorListOption}
                  placeholder="请选择"
                  value={selectedMonitor?.value}
                  style={{ width: 160 }}
                  onChange={(value, option) => {
                    setSelectedMonitor(option as unknown as MonitorOption);
                  }}
                />
              </div>

              <Button type="link" icon={<FullscreenOutlined />} onClick={() => setIsFullScreen(true)}>
                全屏
              </Button>
            </div>
            <div style={{ marginTop: 20 }}>
              {monitorUrl ? (
                <HlsVideo src={monitorUrl} code={selectedMonitor?.value} height={264} />
              ) : (
                <Empty description="暂无视频" style={{ height: 264 }} />
              )}
            </div>
          </Spin>
        </div>
      )}
      {isFullScreen && (
        <Modal
          open={isFullScreen}
          title={selectedMonitor?.label}
          size="big"
          onCancel={() => setIsFullScreen(false)}
          footer={null}
          destroyOnClose
          maskClosable={false}
        >
          <HlsVideo src={monitorUrl} code={selectedMonitor?.value} height="100%" minHeight={500} />
        </Modal>
      )}
    </div>
  );
};

export default RightContent;
